import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/base/Toast";
import Select from "@/components/base/Select";
import { useStore } from "@/context/StoreContext";
import {
  EMPLOYEE_ROLE_CONFIG,
  EMPLOYEE_ROLE_OPTIONS,
  EMPLOYEE_STATUS_CONFIG,
  EMPLOYEE_STATUS_OPTIONS,
  isValidSaudiPhone,
  normalizeSaudiPhone,
} from "@/constants/employees";
import PermissionChecklist from "@/pages/employees/components/PermissionChecklist";
import type {
  Employee,
  EmployeeRole,
  EmployeeStatus,
  PermissionKey,
} from "@/types";

interface EmployeeFormScreenProps {
  employee: Employee | null;
  defaultBranchId?: string;
  focus?: "permissions" | null;
}

interface FormState {
  name: string;
  phone: string;
  branchId: string;
  role: EmployeeRole | "";
  status: EmployeeStatus;
  permissions: PermissionKey[];
}

const inputClass =
  "w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300";

export default function EmployeeFormScreen({
  employee,
  defaultBranchId = "",
  focus = null,
}: EmployeeFormScreenProps) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { employees, branches, saveEmployee } = useStore();

  const isEdit = Boolean(employee);
  const [form, setForm] = useState<FormState>(() =>
    employee
      ? {
          name: employee.name,
          phone: employee.phone,
          branchId: employee.branchId,
          role: employee.role,
          status: employee.status,
          permissions: employee.permissions,
        }
      : {
          name: "",
          phone: "",
          branchId: defaultBranchId,
          role: "",
          status: "active",
          permissions: [],
        },
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (focus !== "permissions") return;
    const target = document.getElementById("employee-permissions");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focus]);

  const goBack = () => navigate("/employees");

  const handleRoleChange = (role: EmployeeRole) => {
    setForm((prev) => ({
      ...prev,
      role,
    }));
  };

  const handlePermissionsChange = (next: PermissionKey[]) => {
    setForm((prev) => ({
      ...prev,
      permissions: next,
    }));
  };

  const isDuplicatePhone = useMemo(() => {
    const normalized = normalizeSaudiPhone(form.phone);
    if (!normalized) return false;
    return employees.some(
      (item) =>
        normalizeSaudiPhone(item.phone) === normalized &&
        item.id !== employee?.id,
    );
  }, [employees, form.phone, employee?.id]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setError("الرجاء إدخال اسم الموظف");
      return;
    }
    if (!form.phone.trim()) {
      setError("الرجاء إدخال رقم الجوال");
      return;
    }
    if (!isValidSaudiPhone(form.phone)) {
      setError("الرجاء إدخال رقم جوال سعودي صحيح، مثال: 05XXXXXXXX");
      return;
    }
    if (isDuplicatePhone) {
      setError("رقم الجوال مرتبط بحساب موظف آخر");
      return;
    }
    if (!form.branchId) {
      setError("يرجى اختيار الفرع");
      return;
    }
    if (!form.role) {
      setError("يرجى اختيار الدور");
      return;
    }

    try {
      saveEmployee({
        id: employee?.id ?? `emp${Date.now()}`,
        name,
        phone: normalizeSaudiPhone(form.phone),
        branchId: form.branchId,
        role: form.role,
        status: form.status,
        permissions: form.permissions,
        createdAt: employee?.createdAt ?? "٢١ سبتمبر ٢٠٢٦",
        lastLogin: employee?.lastLogin ?? "لم يسجّل الدخول بعد",
      });
      show(
        isEdit
          ? "تم حفظ تعديلات الموظف"
          : "تمت إضافة الموظف وربطه بالفرع",
      );
      goBack();
    } catch {
      setError("تعذر حفظ بيانات الموظف، حاول مرة أخرى");
    }
  };

  return (
    <div className="space-y-4">
      <div className="yc-fade-up flex items-center gap-3">
        <button
          type="button"
          onClick={goBack}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          aria-label="رجوع"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-foreground-950">
            {isEdit ? "تعديل بيانات الموظف" : "إضافة موظف جديد"}
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            {isEdit
              ? "عدّل بيانات الموظف ودوره وصلاحياته ثم احفظ التغييرات"
              : "اربط الموظف بالفرع وحدد دوره وصلاحياته"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5 md:p-6">
          <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-user-3-line"></i>
            </span>
            بيانات الموظف
          </h3>

          <div className="mt-5 max-w-2xl space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-2">
                  اسم الموظف <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                  placeholder="مثال: محمد العتيبي"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-2">
                  رقم الجوال السعودي <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  dir="ltr"
                  value={form.phone}
                  onChange={(event) =>
                    setForm({ ...form, phone: event.target.value })
                  }
                  placeholder="05XXXXXXXX"
                  className={`${inputClass} text-left`}
                />
                {isDuplicatePhone ? (
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-500">
                    <i className="ri-error-warning-line"></i>
                    رقم الجوال مرتبط بحساب موظف آخر
                  </p>
                ) : (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-information-line"></i>
                    يتم تسجيل الدخول برقم الجوال ورمز OTP، بدون كلمة مرور.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-2">
                  الفرع <span className="text-red-500">*</span>
                </label>
                <Select
                  value={form.branchId}
                  options={branches.map((branch) => ({
                    value: branch.id,
                    label: branch.name,
                  }))}
                  onChange={(value) => setForm({ ...form, branchId: value })}
                  placeholder="اختر الفرع"
                  icon="ri-store-2-line"
                  ariaLabel="الفرع"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-2">
                  حالة الحساب
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {EMPLOYEE_STATUS_OPTIONS.map((statusKey) => {
                    const status = EMPLOYEE_STATUS_CONFIG[statusKey];
                    const active = form.status === statusKey;
                    return (
                      <button
                        key={statusKey}
                        type="button"
                        onClick={() => setForm({ ...form, status: statusKey })}
                        className={`flex items-center gap-2.5 rounded-btn border px-4 py-3 text-sm font-semibold transition-colors duration-200 cursor-pointer ${
                          active
                            ? "border-primary-500 bg-primary-50 text-primary-800"
                            : "border-background-200 bg-white text-foreground-600 hover:border-background-400"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${status.dot}`}
                        ></span>
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="max-w-2xl">
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                الدور الوظيفي <span className="text-red-500">*</span>
              </label>
              <Select
                value={form.role}
                options={EMPLOYEE_ROLE_OPTIONS.map((roleKey) => ({
                  value: roleKey,
                  label: EMPLOYEE_ROLE_CONFIG[roleKey].label,
                  description: EMPLOYEE_ROLE_CONFIG[roleKey].description,
                }))}
                onChange={(value) => handleRoleChange(value as EmployeeRole)}
                placeholder="اختر الدور الوظيفي"
                icon="ri-shield-user-line"
                ariaLabel="الدور الوظيفي"
              />
              {form.role ? (
                <p className="mt-2 flex items-start gap-1.5 text-xs leading-5 text-foreground-500">
                  <i className="ri-information-line mt-0.5"></i>
                  <span>{EMPLOYEE_ROLE_CONFIG[form.role].description}</span>
                </p>
              ) : (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-information-line"></i>
                  اختر المسمى الوظيفي؛ المسمى وصفي ولا يمنح أي صلاحية تلقائيًا.
                </p>
              )}
            </div>
          </div>
        </section>

        <section
          id="employee-permissions"
          className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5 md:p-6 scroll-mt-24"
        >
          <div className="flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-800">
                <i className="ri-shield-check-line"></i>
              </span>
              الصلاحيات الممنوحة
            </h3>
            <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
              {form.permissions.length} صلاحية
            </span>
          </div>

          <div className="mt-5 max-w-3xl">
            {form.role ? (
              <>
                <p className="mb-4 rounded-box bg-background-50 border border-background-200 px-4 py-3 text-xs leading-6 text-foreground-600">
                  المسمى الوظيفي وصفي فقط ولا يمنح أي صلاحية تلقائيًا. حدّد بنفسك
                  الصلاحيات الفعلية التي تريد منحها لهذا الموظف.
                </p>
                <PermissionChecklist
                  value={form.permissions}
                  onChange={handlePermissionsChange}
                />
              </>
            ) : (
              <div className="rounded-box border border-dashed border-background-300 bg-background-50 px-4 py-10 text-center">
                <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <p className="text-sm text-foreground-500">
                  اختر المسمى الوظيفي أولًا لعرض قائمة الصلاحيات.
                </p>
              </div>
            )}
          </div>
        </section>

        <div className="yc-fade-up flex flex-col gap-3 rounded-card border border-background-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          {error ? (
            <p className="flex items-center gap-2 text-sm font-medium text-red-600">
              <i className="ri-error-warning-line"></i>
              {error}
            </p>
          ) : (
            <p className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-information-line"></i>
              المسمى الوظيفي وصفي فقط، وأنت من يحدد الصلاحيات الفعلية لكل موظف.
            </p>
          )}
          <div className="flex gap-3 sm:shrink-0">
            <button
              type="button"
              onClick={goBack}
              className="flex-1 sm:flex-none h-11 px-6 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="flex-1 sm:flex-none h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {isEdit ? "حفظ التعديلات" : "إضافة الموظف"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}